import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { Table, THead, TH, TR, TD } from '@/components/ui/table';
import {
  CK_ROUTES,
  CK_PERMISSIONS,
  StockTransferStatus,
  TransferDiscrepancyResolution,
  apiMessage,
  newOperationId,
  type StockTransfer,
} from '../centralKitchenApi';
import { transferStatusTone, formatDateTime, formatQuantity } from '../lib';

interface Props {
  transferId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * ONE TRANSFER, dispatched and received quantities per line. A discrepancy —
 * `receivedQuantity !== quantity` — is shown as a fact of the document, not
 * folded into a single "did it arrive" checkmark, because ADR-0015 §4 is
 * explicit that the difference is resolved, never absorbed: `Resolve` is
 * offered only once the transfer is `DISCREPANT`, asks which of three things
 * happened, and is the ONLY action here that can change chain-wide stock.
 */
export default function TransferDetailDialog({ transferId, onClose, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { prompt } = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveQty, setReceiveQty] = useState<Record<string, string>>({});
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState<TransferDiscrepancyResolution>(
    TransferDiscrepancyResolution.WRITTEN_OFF,
  );
  const [resolutionNotes, setResolutionNotes] = useState('');

  const transfer = useApi<StockTransfer>(transferId ? CK_ROUTES.transfer(transferId) : null);

  useEffect(() => {
    if (transfer.data) {
      const defaults: Record<string, string> = {};
      for (const line of transfer.data.lines ?? []) defaults[line.inventoryItemId] = line.quantity;
      setReceiveQty(defaults);
    }
  }, [transfer.data]);

  if (!transferId) return null;

  const reload = () => {
    transfer.reload();
    onChanged();
  };

  const dispatch = async () => {
    setBusy('dispatch');
    try {
      await api.post(CK_ROUTES.transferDispatch(transferId), {
        operationId: newOperationId('ck-dispatch'),
      });
      toast.success(t('centralKitchen.transfers.dispatched'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const submitReceive = async () => {
    setBusy('receive');
    try {
      const lines = (transfer.data?.lines ?? []).map((line) => ({
        inventoryItemId: line.inventoryItemId,
        receivedQuantity: (receiveQty[line.inventoryItemId] ?? line.quantity).trim(),
      }));
      await api.post(CK_ROUTES.transferReceive(transferId), {
        lines,
        operationId: newOperationId('ck-receive'),
      });
      toast.success(t('centralKitchen.transfers.received'));
      setReceiveOpen(false);
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const submitResolve = async () => {
    if (resolutionNotes.trim().length < 4) return;
    setBusy('resolve');
    try {
      await api.post(CK_ROUTES.transferResolve(transferId), {
        resolution,
        notes: resolutionNotes.trim(),
        operationId: newOperationId('ck-resolve'),
      });
      toast.success(t('centralKitchen.transfers.resolved'));
      setResolveOpen(false);
      setResolutionNotes('');
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    const reason = await prompt({
      title: t('centralKitchen.transfers.cancelTitle'),
      label: t('centralKitchen.transfers.cancelReasonLabel'),
      confirmLabel: t('centralKitchen.transfers.cancelConfirm'),
      tone: 'danger',
    });
    if (reason == null) return;
    setBusy('cancel');
    try {
      await api.post(CK_ROUTES.transferCancel(transferId), { reason: reason.trim() || undefined });
      toast.success(t('centralKitchen.transfers.cancelled'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const doc = transfer.data;
  const canDispatch = can(CK_PERMISSIONS.transfersDispatch) && doc?.status === StockTransferStatus.DRAFT;
  const canReceive = can(CK_PERMISSIONS.transfersReceive) && doc?.status === StockTransferStatus.IN_TRANSIT;
  const canResolve = can(CK_PERMISSIONS.transfersResolve) && doc?.status === StockTransferStatus.DISCREPANT;
  const canCancel = can(CK_PERMISSIONS.transfersCancel) && doc?.status === StockTransferStatus.DRAFT;

  return (
    <Dialog
      open={Boolean(transferId)}
      onClose={onClose}
      title={doc ? t('centralKitchen.transfers.detailTitle', { number: doc.number }) : t('common.loading')}
      className="max-w-2xl"
    >
      {transfer.loading ? (
        <LoadingState />
      ) : transfer.error || !doc ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
          {transfer.error ?? t('centralKitchen.states.loadFailedHint')}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={transferStatusTone(doc.status)}>{t(`centralKitchen.transferStatus.${doc.status}`)}</Badge>
            <span className="text-sm text-ink-muted">
              {doc.fromWarehouse?.name ?? doc.fromWarehouseId} → {doc.toWarehouse?.name ?? doc.toWarehouseId}
            </span>
          </div>

          {doc.status === StockTransferStatus.DISCREPANT && (
            <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              {t('centralKitchen.transfers.discrepantHint')}
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <THead>
                <TR>
                  <TH>{t('centralKitchen.transfers.columns.item')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.dispatchedQty')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.receivedQty')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.discrepancy')}</TH>
                </TR>
              </THead>
              <tbody>
                {(doc.lines ?? []).map((line) => (
                  <TR key={line.id}>
                    <TD>{line.inventoryItem?.name ?? line.inventoryItemId}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(line.quantity, i18n.language)}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(line.receivedQuantity, i18n.language)}</TD>
                    <TD
                      className={`text-end tabular-nums ${
                        line.discrepancyQuantity && Number(line.discrepancyQuantity) !== 0
                          ? 'font-medium text-amber-600 dark:text-amber-400'
                          : ''
                      }`}
                    >
                      {line.discrepancyQuantity ? formatQuantity(line.discrepancyQuantity, i18n.language) : '—'}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink-muted">
            <p>{t('centralKitchen.transfers.columns.dispatched')}: {formatDateTime(doc.dispatchedAt, i18n.language)}</p>
            <p>{t('centralKitchen.transfers.columns.received')}: {formatDateTime(doc.receivedAt, i18n.language)}</p>
            {doc.resolution && (
              <p className="col-span-2">
                {t('centralKitchen.transfers.resolvedAs', { resolution: t(`centralKitchen.resolution.${doc.resolution}`) })}
                {doc.resolutionNotes ? ` — ${doc.resolutionNotes}` : ''}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 border-t pt-4">
            {canDispatch && (
              <Button size="sm" onClick={dispatch} disabled={busy !== null}>
                {t('centralKitchen.transfers.dispatch')}
              </Button>
            )}
            {canReceive && (
              <Button size="sm" onClick={() => setReceiveOpen(true)} disabled={busy !== null}>
                {t('centralKitchen.transfers.receive')}
              </Button>
            )}
            {canResolve && (
              <Button size="sm" variant="danger" onClick={() => setResolveOpen(true)} disabled={busy !== null}>
                {t('centralKitchen.transfers.resolve')}
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="outline" onClick={cancel} disabled={busy !== null}>
                {t('centralKitchen.transfers.cancel')}
              </Button>
            )}
          </div>
        </div>
      )}

      {receiveOpen && doc && (
        <Dialog
          open
          onClose={() => setReceiveOpen(false)}
          title={t('centralKitchen.transfers.receiveTitle')}
          description={t('centralKitchen.transfers.receiveDescription')}
          className="max-w-lg"
          footer={
            <>
              <Button variant="outline" onClick={() => setReceiveOpen(false)} disabled={busy !== null}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitReceive} disabled={busy !== null}>
                {t('centralKitchen.transfers.receiveConfirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            {(doc.lines ?? []).map((line) => (
              <Field
                key={line.id}
                label={line.inventoryItem?.name ?? line.inventoryItemId}
                hint={t('centralKitchen.transfers.dispatchedWas', { qty: formatQuantity(line.quantity, i18n.language) })}
                controlId={`receive-${line.id}`}
              >
                <Input
                  inputMode="decimal"
                  value={receiveQty[line.inventoryItemId] ?? line.quantity}
                  onChange={(e) => setReceiveQty((q) => ({ ...q, [line.inventoryItemId]: e.target.value }))}
                />
              </Field>
            ))}
          </div>
        </Dialog>
      )}

      {resolveOpen && (
        <Dialog
          open
          onClose={() => setResolveOpen(false)}
          title={t('centralKitchen.transfers.resolveTitle')}
          description={t('centralKitchen.transfers.resolveDescription')}
          className="max-w-lg"
          footer={
            <>
              <Button variant="outline" onClick={() => setResolveOpen(false)} disabled={busy !== null}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={submitResolve}
                disabled={busy !== null || resolutionNotes.trim().length < 4}
              >
                {t('centralKitchen.transfers.resolveConfirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label={t('centralKitchen.transfers.resolutionLabel')} controlId="ck-resolution">
              <Select value={resolution} onChange={(e) => setResolution(e.target.value as TransferDiscrepancyResolution)}>
                <option value={TransferDiscrepancyResolution.WRITTEN_OFF}>
                  {t('centralKitchen.resolution.WRITTEN_OFF')}
                </option>
                <option value={TransferDiscrepancyResolution.RETURNED_TO_ORIGIN}>
                  {t('centralKitchen.resolution.RETURNED_TO_ORIGIN')}
                </option>
                <option value={TransferDiscrepancyResolution.ACCEPTED_AS_FOUND}>
                  {t('centralKitchen.resolution.ACCEPTED_AS_FOUND')}
                </option>
              </Select>
            </Field>
            <Field
              label={t('centralKitchen.transfers.resolutionNotes')}
              hint={t('centralKitchen.transfers.resolutionNotesHint')}
              controlId="ck-resolution-notes"
            >
              <Textarea rows={3} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}
